/**
 * 智能体状态管理 Store
 *
 * 使用 zustand 管理智能体状态，包括智能体列表、当前选中的智能体、加载状态等。
 * 通过 REST API 进行智能体 CRUD 操作，使用 HttpOnly Cookie 认证。
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { agentApi } from '../services/agentApi';
import { sanitizeErrorMessage } from '../utils/validation';
import type { Agent, AgentCreate, AgentUpdate, ToolsListResponse, SubagentInfo } from '../types/agent';

// 子智能体会话状态
export interface SubagentSessionState {
  sessionId: string;
  parentSessionId?: string;  // 父会话ID，用于关联到特定聊天会话
  agentId: number;
  agentName: string;
  status: 'pending' | 'running' | 'completed' | 'failed' | 'timeout';
  result?: string;
  error?: string;
  startedAt: string;
  completedAt?: string;
  duration?: number;
  // 执行过程数据
  streamContent?: string;  // 流式输出内容
  toolCalls?: Array<{
    tool_call_id?: string;
    tool_name: string;
    status: 'detected' | 'executing' | 'success' | 'failed';
    tool_args?: Record<string, unknown>;
    result?: unknown;
    error?: string;
    timestamp: string;
  }>;
  messages?: Array<{
    role: 'assistant' | 'tool';
    content?: string;
    tool_name?: string;
    timestamp: string;
  }>;
}

// Store 状态接口
interface AgentState {
  // 状态
  agents: Agent[];
  subagents: SubagentInfo[];
  subagentSessions: Map<string, SubagentSessionState>;
  currentAgentId: number | null;
  toolsList: ToolsListResponse | null;
  loading: boolean;
  error: string | null;

  // 计算属性
  getCurrentAgent: () => Agent | null;
  getSystemAgents: () => Agent[];
  getUserAgents: () => Agent[];
  getSubagents: () => SubagentInfo[];
  getSubagentSession: (sessionId: string) => SubagentSessionState | undefined;
  getActiveSubagentSessions: () => SubagentSessionState[];
  getSubagentSessionsByParent: (parentSessionId: string) => SubagentSessionState[];

  // 操作方法
  fetchAgents: () => Promise<void>;
  fetchSubagents: () => Promise<void>;
  createAgent: (data: AgentCreate) => Promise<Agent>;
  updateAgent: (agentId: number, data: AgentUpdate) => Promise<Agent>;
  deleteAgent: (agentId: number) => Promise<void>;
  setCurrentAgent: (agentId: number | null) => void;
  fetchToolsList: (agentId?: number) => Promise<void>;
  clearError: () => void;
  cleanup: () => void;

  // 子智能体会话管理
  updateSubagentSession: (sessionId: string, state: Partial<SubagentSessionState>) => void;
  removeSubagentSession: (sessionId: string) => void;
  clearSubagentSessions: () => void;
}

// 创建智能体 Store（带持久化）
export const useAgentStore = create<AgentState>()(
  persist(
    (set, get) => ({
      // 初始状态
      agents: [],
      subagents: [],
      subagentSessions: new Map(),
      currentAgentId: null,
      toolsList: null,
      loading: false,
      error: null,

      // 计算属性：获取当前选中的智能体
      getCurrentAgent: () => {
        const { agents, currentAgentId } = get();
        if (!currentAgentId) return null;
        return agents.find(a => a.id === currentAgentId) || null;
      },

      // 计算属性：获取系统智能体
      getSystemAgents: () => {
        return get().agents.filter(a => a.is_system);
      },

      // 计算属性：获取用户自定义智能体
      getUserAgents: () => {
        return get().agents.filter(a => !a.is_system);
      },

      // 计算属性：获取子智能体列表
      getSubagents: () => {
        return get().subagents;
      },

      // 计算属性：获取子智能体会话状态
      getSubagentSession: (sessionId: string) => {
        return get().subagentSessions.get(sessionId);
      },

      // 计算属性：获取所有活跃的子智能体会话
      getActiveSubagentSessions: () => {
        const sessions = get().subagentSessions;
        return Array.from(sessions.values()).filter(
          s => s.status === 'pending' || s.status === 'running'
        );
      },

      // 计算属性：获取指定父会话的子智能体会话
      getSubagentSessionsByParent: (parentSessionId: string) => {
        const sessions = get().subagentSessions;
        return Array.from(sessions.values()).filter(
          s => s.parentSessionId === parentSessionId
        );
      },

      // 获取智能体列表
      fetchAgents: async () => {
        const state = get();

        // 如果已经有数据且没有错误，跳过请求
        if (state.agents.length > 0 && !state.error) {
          return;
        }

        // 如果正在请求中，直接返回（防止并发请求）
        if (state.loading) {
          return;
        }

        set({ loading: true, error: null });
        try {
          const agents = await agentApi.listAgents();

          set({ agents, loading: false });

          // 如果没有选中智能体，默认选中第一个系统智能体（写作）
          const { currentAgentId } = get();
          if (!currentAgentId && agents.length > 0) {
            const writingAgent = agents.find(a => a.is_system && a.name === '写作');
            set({ currentAgentId: writingAgent?.id || agents[0].id });
          }

          // 同时获取子智能体列表
          get().fetchSubagents();
        } catch (error) {
          const errorMessage = sanitizeErrorMessage(error, '获取智能体列表失败，请稍后重试');
          set({ error: errorMessage, loading: false });
          throw error;
        }
      },

      // 获取子智能体列表
      fetchSubagents: async () => {
        try {
          const subagents = await agentApi.listSubagents();
          set({ subagents });
        } catch (error) {
          console.error('获取子智能体列表失败:', error);
          // 不设置错误，子智能体列表获取失败不影响主流程
        }
      },

      // 创建智能体
      createAgent: async (data: AgentCreate) => {
        set({ loading: true, error: null });
        try {
          const newAgent = await agentApi.createAgent(data);

          set(state => ({
            agents: [...state.agents, newAgent],
            loading: false,
          }));

          // 如果创建的是子智能体，刷新子智能体列表
          if (data.mode === 'subagent' || data.mode === 'all') {
            get().fetchSubagents();
          }

          return newAgent;
        } catch (error) {
          const errorMessage = sanitizeErrorMessage(error, '创建智能体失败，请稍后重试');
          set({ error: errorMessage, loading: false });
          throw error;
        }
      },

      // 更新智能体
      updateAgent: async (agentId: number, data: AgentUpdate) => {
        set({ loading: true, error: null });
        try {
          const updatedAgent = await agentApi.updateAgent(agentId, data);

          set(state => ({
            agents: state.agents.map(a =>
              a.id === agentId ? updatedAgent : a
            ),
            loading: false,
          }));

          // 刷新子智能体列表
          get().fetchSubagents();

          return updatedAgent;
        } catch (error) {
          const errorMessage = sanitizeErrorMessage(error, '更新智能体失败，请稍后重试');
          set({ error: errorMessage, loading: false });
          throw error;
        }
      },

      // 删除智能体
      deleteAgent: async (agentId: number) => {
        set({ loading: true, error: null });
        try {
          await agentApi.deleteAgent(agentId);

          set(state => ({
            agents: state.agents.filter(a => a.id !== agentId),
            currentAgentId: state.currentAgentId === agentId ? null : state.currentAgentId,
            loading: false,
          }));

          // 刷新子智能体列表
          get().fetchSubagents();
        } catch (error) {
          const errorMessage = sanitizeErrorMessage(error, '删除智能体失败，请稍后重试');
          set({ error: errorMessage, loading: false });
          throw error;
        }
      },

      // 设置当前选中的智能体
      setCurrentAgent: (agentId: number | null) => {
        set({ currentAgentId: agentId });
      },

      // 获取工具列表
      fetchToolsList: async (agentId?: number) => {
        try {
          // 如果有 agentId，获取该智能体的工具列表
          if (agentId) {
            const toolsList = await agentApi.getToolsList(agentId);
            set({ toolsList });
          } else {
            // 否则使用默认工具列表
            const toolsList = agentApi.getDefaultToolsList();
            set({ toolsList });
          }
        } catch (error) {
          // 如果获取失败，使用默认工具列表
          const toolsList = agentApi.getDefaultToolsList();
          set({ toolsList });
        }
      },

      // 清除错误信息
      clearError: () => {
        set({ error: null });
      },

      // 清理资源
      cleanup: () => {
        set({
          agents: [],
          subagents: [],
          subagentSessions: new Map(),
          currentAgentId: null,
          loading: false,
          error: null,
        });
      },

      // 更新子智能体会话状态
      updateSubagentSession: (sessionId: string, state: Partial<SubagentSessionState>) => {
        console.log('[agentStore] updateSubagentSession - sessionId:', sessionId, 'state:', state);
        set(prev => {
          const sessions = new Map(prev.subagentSessions);
          const existing = sessions.get(sessionId);
          if (existing) {
            sessions.set(sessionId, { ...existing, ...state });
            console.log('[agentStore] 更新现有会话:', sessionId, '新状态:', { ...existing, ...state });
          } else if (state.agentId && state.agentName && state.status && state.startedAt) {
            // 创建新的会话状态
            const newSession: SubagentSessionState = {
              sessionId,
              agentId: state.agentId,
              agentName: state.agentName,
              status: state.status,
              startedAt: state.startedAt,
              ...state,
            } as SubagentSessionState;
            sessions.set(sessionId, newSession);
            console.log('[agentStore] 创建新会话:', sessionId, '状态:', newSession);
          } else {
            console.warn('[agentStore] 无法创建会话，缺少必要字段:', { sessionId, state });
          }
          console.log('[agentStore] 当前会话总数:', sessions.size);
          return { subagentSessions: sessions };
        });
      },

      // 移除子智能体会话
      removeSubagentSession: (sessionId: string) => {
        set(prev => {
          const sessions = new Map(prev.subagentSessions);
          sessions.delete(sessionId);
          return { subagentSessions: sessions };
        });
      },

      // 清除所有子智能体会话
      clearSubagentSessions: () => {
        set({ subagentSessions: new Map() });
      },
    }),
    {
      name: 'agent-store',
      partialize: (state) => ({
        currentAgentId: state.currentAgentId,
      }),
    }
  )
);

export default useAgentStore;